/** @typedef {import("../../../api/types").OrderEvent} OrderEvent */
import React, {useCallback, useEffect, useState} from "react";
import axios from "axios";
import {Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner} from "reactstrap";
import IconLabel from "../../../shared/components/IconLabel";
import {XIcon} from "../../../shared/components/icons";
import {describeEvent} from "../utils/orderHistory";

/**
 * Everything that happened to an order, oldest first: fetched when it opens,
 * so it is always the current history.
 * @param {{orderId: number, number: number, currency?: string, onClose: () => void}} props
 */
const OrderHistoryModal = ({orderId, number, currency, onClose}) => {
    const [events, setEvents] = useState(/** @type {OrderEvent[]|null} */ (null));
    const [failed, setFailed] = useState(false);

    const load = useCallback(
        (signal) => {
            setFailed(false);
            setEvents(null);
            axios
                .get(`/os/api/service_orders/${orderId}/events/`, {signal})
                .then(({data}) => setEvents(data))
                .catch((err) => {
                    if (!axios.isCancel(err)) setFailed(true);
                });
        },
        [orderId]
    );

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    return (
        <Modal isOpen toggle={onClose} fade={false} centered scrollable size="lg">
            <ModalHeader toggle={onClose}>Histórico da ordem nº {number}</ModalHeader>
            <ModalBody>
                {events === null && !failed && (
                    <div className="text-center">
                        <Spinner />
                    </div>
                )}
                {failed && (
                    <Alert color="danger" className="mb-0">
                        Não foi possível carregar o histórico.{" "}
                        <Button color="link" className="p-0 align-baseline" onClick={() => load(undefined)}>
                            Tentar de novo
                        </Button>
                    </Alert>
                )}
                {events?.length === 0 && <p className="text-muted mb-0">Nenhum registro.</p>}
                {events && events.length > 0 && (
                    <ol className="list-unstyled mb-0">
                        {events.map((event) => {
                            const {title, who, when, details} = describeEvent(event, currency);
                            return (
                                <li key={event.id} className="border-bottom py-2">
                                    <div className="fw-semibold">{title}</div>
                                    <div className="small text-muted">
                                        {who} · {when}
                                    </div>
                                    {details.length > 0 && (
                                        <ul className="small mb-0 mt-1 ps-3">
                                            {details.map((line) => (
                                                <li key={line}>{line}</li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={onClose}>
                    <IconLabel icon={<XIcon />}>Fechar</IconLabel>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default OrderHistoryModal;
